import React, { useState, useEffect, useRef } from 'react'
import { Modal, ModalHeader, ModalBody, Button, Input } from "reactstrap";
import Select from "react-select";
import ModalCloseIcon from "../../common/Components/Icons/ModalCloseIcon";
import { connect } from "react-redux";
//components
import { AddIcon } from '../../common/Components/Icons/AddIcon';
import Loader from "../../common/Components/Loader/Loader";
import notifications from "../../common/Components/Notifications/Notification";
// actions
import {
    getCatAlogue,
    createCatAlogue,
    addProductByCatAlogueId
} from "../../Store/catelogue/actions";
import { isArray } from 'lodash';
import { trackCatalogueCreated } from "../../utils/cataloguePublicAnalytics";

type Option = { label: string; value: string };

const toOption = (ele: any): Option => ({
    label: [ele?.name, ele?.description].filter(Boolean).join(" "),
    value: ele?._id
});

/** Most recently used catalogues first (server sorts by lastUsedAt, then newest). */
const LIST_QUERY = { offset: 0, limit: 100, sortBy: "lastUsedAt", sortOrder: "desc" };

export const CatalogueListModal = (props: any) => {
    const { openModal, closeModal, catalogueProduct, getProductData } = props;
    const [loading, setLoading]: any = useState(false)
    const [options, setOptions] = useState<Option[]>([]);
    const [catalogueName, setCatalogueName]: any = useState(null);
    const [error, setError] = useState("");
    // Inline "new catalogue": the + opens a name field; on success the new catalogue is selected.
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState("");
    const [createError, setCreateError] = useState("");
    const [createBusy, setCreateBusy] = useState(false);
    // Typing in the dropdown searches the server, so studios with many catalogues can find any of them.
    const [searching, setSearching] = useState(false);
    const searchTimer = useRef<number | undefined>(undefined);
    const latestSearch = useRef("");

    useEffect(() => {
        if (openModal) {
            setCatalogueName(null);
            setError("");
            setCreating(false);
            setNewName("");
            setCreateError("");
            getAllData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openModal])

    const toOptions = (res: any): Option[] => {
        const docs = isArray(res?.data?.docs) ? res.data.docs : [];
        const seen = new Set<string>();
        // In stock / Out of stock catalogues fill themselves; products can't be added to them by hand.
        return docs
            .filter((ele: any) => !ele?.systemManaged)
            .map(toOption)
            .filter((o: Option) => o.value && !seen.has(o.value) && seen.add(o.value));
    }

    /** A server without lastUsedAt sorting rejects it; fall back to newest first rather than an empty list. */
    const fetchCatalogues = (extra: any, done: (res: any) => void) => {
        props.getCatAlogue({ ...LIST_QUERY, ...extra }, (res: any) => {
            if (res?.success === false) {
                props.getCatAlogue({ ...LIST_QUERY, ...extra, sortBy: "createdAt" }, done);
                return;
            }
            done(res);
        });
    }

    const getAllData = (selectId?: string) => {
        setLoading(true)
        latestSearch.current = "";
        fetchCatalogues({}, (res: any) => {
            setLoading(false)
            const next = toOptions(res);
            setOptions(next);
            if (selectId) {
                setCatalogueName(next.find((o: Option) => o.value === selectId) || null);
            }
        })
    }

    const searchCatalogues = (text: string) => {
        const query = text.trim();
        if (query === latestSearch.current) return;
        latestSearch.current = query;
        window.clearTimeout(searchTimer.current);
        searchTimer.current = window.setTimeout(() => {
            setSearching(true);
            fetchCatalogues({ search: query }, (res: any) => {
                // Ignore answers to an older search.
                if (latestSearch.current !== query) return;
                setSearching(false);
                setOptions(toOptions(res));
            });
        }, 300);
    }

    useEffect(() => () => window.clearTimeout(searchTimer.current), []);

    const createCatalogue = () => {
        const name = newName.trim();
        if (name.length < 3) {
            setCreateError("Catalogue name must be at least 3 characters long.");
            return;
        }
        if (name.length > 100) {
            setCreateError("Catalogue name must not exceed 100 characters.");
            return;
        }
        setCreateBusy(true);
        setCreateError("");
        props.createCatAlogue(
            { name, description: "", category: "", targetMarket: "", createdAt: new Date().toISOString() },
            (res: any) => {
                setCreateBusy(false);
                if (!res?.success) {
                    setCreateError(res?.message || "Catalogue could not be created. Please try again.");
                    return;
                }
                const createdId = res?.data?.metadata?.catalogueId || res?.data?.catalogue?._id || res?.data?.catalogue?.id;
                if (createdId) {
                    trackCatalogueCreated(String(createdId));
                }
                notifications({ title: "Success!", message: "Catalogue created.", type: "success" });
                setCreating(false);
                setNewName("");
                setError("");
                getAllData(createdId ? String(createdId) : undefined);
            }
        );
    }

    const handleSubmit = (e: any) => {
        e.preventDefault();
        if (createBusy) {
            return;
        }
        if (!catalogueName?.value) {
            setError("Choose a catalogue, or create one with +.");
            return;
        }
        setLoading(true)
        props.addProductByCatAlogueId({
            productIds: [
                catalogueProduct
            ]
        }, catalogueName?.value, (res: any) => {
            setLoading(false);
            if (res.success) {
                notifications({
                    title: "Success!",
                    message: res?.message,
                    type: "success",
                });
                props.closeModal();
                getProductData();
            } else {
                notifications({
                    title: "Error!",
                    message: res?.message,
                    type: "danger",
                });
            }
        })
    }
    return (
        <Modal
            isOpen={openModal}
            toggle={closeModal}
            size="lg"
            className="CatalogueModal"
            fade={false}
        >
            <ModalHeader>
                <div className="Heading">
                    <h3>Add Product Into Catalogue</h3>
                    <ModalCloseIcon {...props} closeModal={closeModal} />
                </div>
            </ModalHeader>
            <ModalBody>
                <div className="AddCatalogue">
                    <form className="form" onSubmit={handleSubmit}>
                        {loading ? (
                            <div className="LoaderDivModal">
                                <Loader />
                            </div>
                        ) : (
                            <>
                                <div className="formDiv">
                                    <label htmlFor="Type">
                                        Name<span>*</span>
                                    </label>
                                    <div className="formDivSub">
                                        <Select
                                            className="React_selector text-white"
                                            classNamePrefix="react-select"
                                            placeholder="Select Catalogue Name"
                                            isSearchable
                                            name="Type"
                                            inputId="Type"
                                            value={catalogueName}
                                            options={options}
                                            filterOption={null}
                                            isLoading={searching}
                                            onInputChange={(text: string, meta: any) => {
                                                if (meta?.action === "input-change") searchCatalogues(text);
                                                else if (meta?.action === "menu-close" && latestSearch.current) searchCatalogues("");
                                            }}
                                            noOptionsMessage={({ inputValue }: any) =>
                                                inputValue ? "No catalogue matches. Create one with +." : "No catalogues yet. Create one with +."}
                                            onChange={(e: any) => {
                                                setCatalogueName(e)
                                                setError("")
                                            }}

                                        />
                                        <div className="DivSep"></div>
                                        <div
                                            className="formAddDiv"
                                            role="button"
                                            tabIndex={0}
                                            aria-label="Create a new catalogue"
                                            aria-expanded={creating}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => setCreating((open) => !open)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    setCreating((open) => !open);
                                                }
                                            }}
                                        >
                                            {/* The whole box is the button; the icon itself has no handler, so a click toggles once. */}
                                            <AddIcon />
                                        </div>
                                    </div>
                                    {error && !catalogueName ? (
                                        <p className="RequiredField">{error}</p>
                                    ) : (
                                        ""
                                    )}
                                </div>
                                {creating && (
                                    <div className="formDiv">
                                        <label htmlFor="new-catalogue-name">New catalogue name</label>
                                        <div className="d-flex gap-2 align-items-start">
                                            <Input
                                                id="new-catalogue-name"
                                                autoFocus
                                                value={newName}
                                                maxLength={100}
                                                placeholder="e.g. Wedding collection 2026"
                                                onChange={(e) => {
                                                    setNewName(e.target.value);
                                                    setCreateError("");
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        createCatalogue();
                                                    }
                                                }}
                                            />
                                            <Button type="button" color="primary" disabled={createBusy} onClick={createCatalogue} style={{ whiteSpace: "nowrap", minHeight: 44 }}>
                                                {createBusy ? "Creating…" : "Create"}
                                            </Button>
                                        </div>
                                        {createError && <p className="RequiredField" role="alert">{createError}</p>}
                                    </div>
                                )}
                            </>

                        )}
                        <Button className="artButton" type="submit" disabled={loading || createBusy}>
                            {loading ? "Processing" : "Save"}
                        </Button>
                    </form>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default connect(null, {
    getCatAlogue,
    createCatAlogue,
    addProductByCatAlogueId
})(CatalogueListModal);
